// src/ScannerForm.tsx

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";
import {
  Input,
  Form,
  Row,
  Col,
  Typography,
  Switch,
  Card,
  Spin,
  Alert,
  Descriptions,
  Space,
  Select,
  Button,
  Progress,
} from "antd";
// Import the CSS module
import styles from "./warranty.module.css";
import {
  TContext,
  TDropdownOptions,
  TPrivilege,
  TResponseModel,
} from "../../../types/config";
import useFetch from "../../../hooks/useFetch";
import { eOUTPUT_STATUS, ePrivileges, eResultCode } from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { GrPowerReset } from "react-icons/gr";
import {
  CloseOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  CloseCircleFilled,
  HistoryOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  AddEditCrateScanInfo,
  GetCustomerList,
  GetProductList,
  GetScannedCrateList,
  GetScannedProductDetails,
  UpdateCrateMapping,
} from "../../../utils/api.constant";
import DateUtils from "../../../utils/date";
import { TProduct, TScanResult } from "../../../types";
import CustomDrawer from "../../../components/drawer";
import ScannedHistoryForm from "./blower-scanned-history";
import ScannedBlowerList from "./blower-list";
import CrateScannedHistory from "./crate-scanned-history";
import { UserContext } from "../../../context";
import Utils from "../../../utils";
import CustomModal from "../../../components/modal";

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

// --- TypeScript Types & Constants (No changes here) ---
type DisplayMode = "default" | "large" | "xl";
type ScanType = "blower" | "crate";
const zoomMarks = {
  0: { style: { fontSize: 12 }, label: "Default" },
  1: { style: { fontSize: 12 }, label: "Large" },
  2: { style: { fontSize: 12 }, label: "XL" },
};
const displayModes = ["default", "large", "xl"];

// --- The React Component ---
const ScannerForm: React.FC = () => {
  const [form] = Form.useForm();

  // All state management remains the same
  const [scanType, setScanType] = useState<ScanType>(
    () => (localStorage.getItem("scanType") as ScanType) || "blower"
  );
  const [displayMode, setDisplayMode] = useState<DisplayMode>(
    () => (localStorage.getItem("displayMode") as DisplayMode) || "default"
  );
  const [autoClear, setAutoClear] = useState<boolean>(
    () => localStorage.getItem("autoClear") === "true"
  );
  const [autoClearDuration, setAutoClearDuration] = useState<number>(() =>
    parseInt(localStorage.getItem("autoClearDuration") || "10", 10)
  );
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [countdown, setCountdown] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<TScanResult | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [isFullscreen, setIsFullscreen] = useState<boolean>(
    () => localStorage.getItem("isFullscreen") === "true"
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [productId, setProductId] = useState<number | null>(null);
  const [productOptions, setProductOptions] = useState<TDropdownOptions[]>([]);
  const context: TContext = useContext(UserContext);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [selectedCustomer, setSelectedCustomer] = useState<TDropdownOptions>();
  const [crateOptions, setCrateOptions] = useState<any[]>([]);
  // confirmation modal hooks
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [showOldSerialModal, setShowOldSerialModal] = useState<boolean>(false);
  const [isBothErrors, setBothErrors] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [selectedCrateId, setSelectedCrateId] = useState<number | null>(null);

  useEffect(() => {
    fetchCustomerOptions();
    fetchCrateOptions();
  }, []);

  const fetchCrateOptions = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetScannedCrateList,
        requestPayload
      );
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedOptions = data.map((crate: any) => ({
          value: crate.id,
          label: crate.serialNumber,
          data: crate,
        }));
        setCrateOptions(formattedOptions);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomerOptions = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetCustomerList, requestPayload);
      const { dataResponse, data } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        // Transform data to match table columns
        setCustomerOptions(Utils.mapDropdownOptions(data));
        setSelectedCustomer(Utils.mapDropdownOptions?.(data)?.[0]);
        setIsLoading(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      // onShowToast({
      //   type: ToastType.error,
      //   title: <CloseCircleFilled />,
      //   position: ToastOpen.leftBottom,
      //   content: "Failed to fetch customer data",
      // });
      setIsLoading(false);
    }
  };
  // All effects and handlers remain the same
  useEffect(() => {
    localStorage.setItem("scanType", scanType);
  }, [scanType]);

  const fetchScanData = useCallback(
    async (serial: string) => {
      try {
        const requestPayload = {
          data: {
            serialNumber: serial,
            customerId: selectedCustomer?.value,
            productId: scanType === "blower" ? productId : null,
            isProductRetryRequest: false,
            isOldSerialNoRetryRequest: false,
            isIncomingScan: true,
          },
        };
        const endPoint =
          scanType === "blower"
            ? GetScannedProductDetails
            : AddEditCrateScanInfo;
        const response: TResponseModel = await post(endPoint, requestPayload);
        let { dataResponse, data } = response;
        if (
          dataResponse.returnCode === eResultCode.SUCCESS ||
          dataResponse.returnCode === eResultCode.R_DUPLICATE
        ) {
          if (scanType === "crate") {
            data = {
              outputStatus: eOUTPUT_STATUS.GREEN,
            };
          }
          setScanResult({
            ...data,
            responseCode: dataResponse?.returnCode,
            responseMsg: dataResponse.description,
          });
        } else if (
          dataResponse.returnCode === eResultCode.R_SHOWPRODUCTRETRYALERT
        ) {
          // show confirmation modal logic here
          setShowErrorModal(true);
          setErrorMessage(dataResponse?.description);
        } else if (
          dataResponse.returnCode === eResultCode.R_SHOWOLDSERIALRNORETRYALERT
        ) {
          // show confirmation modal logic here
          setShowOldSerialModal(true);
          setErrorMessage(dataResponse?.description);
        } else if (dataResponse.returnCode === eResultCode.R_SHOWOBOTHALERT) {
          setBothErrors(true);
          setShowOldSerialModal(true);
          setErrorMessage(dataResponse?.description);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.leftBottom,
            content: dataResponse.description,
          });
        }
      } catch (error) {
        console.error("Error:", error);
        setLoading(false);
      } finally {
        fetchCrateOptions();
        setLoading(false);
      }
    },
    [scanType, productId, post, onShowToast]
  );

  // when user forced to go with error message and unsuccessful scan result
  const fetchForcedScanData = useCallback(
    async (
      pId: number,
      serialNo: string,
      cId: number,
      isOldSerialForced: boolean = false,
      isBothError: boolean = false
    ) => {
      try {
        let requestPayload;
        setIsLoading(true);
        if (isBothError) {
          requestPayload = {
            data: {
              serialNumber: serialNo,
              productId: pId,
              isIncomingScan: true,
              customerId: cId,
              isProductRetryRequest: true,
              isOldSerialNoRetryRequest: true,
            },
          };
        } else {
          requestPayload = {
            data: {
              serialNumber: serialNo,
              productId: pId,
              isIncomingScan: true,
              customerId: cId,
              isProductRetryRequest: !isOldSerialForced ? true : false,
              isOldSerialNoRetryRequest: isOldSerialForced ? true : false,
            },
          };
        }
        const response: TResponseModel = await post(
          GetScannedProductDetails,
          requestPayload
        );
        let { dataResponse, data } = response;
        if (
          dataResponse.returnCode === eResultCode.SUCCESS ||
          dataResponse.returnCode === eResultCode.R_DUPLICATE
        ) {
          const scanRes = {
            ...data,
            responseCode: dataResponse?.returnCode,
            responseMsg: dataResponse.description,
            loggedOn: new Date(),
          };
          setScanResult(scanRes);
          handleDeleteCancel();
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.leftBottom,
            content: dataResponse.description,
          });
        }
      } catch (error) {
        console.error("Error:", error);
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const handleDeleteCancel = () => {
    setShowErrorModal(false);
    setShowOldSerialModal(false);
    setErrorMessage("");
    setBothErrors(false);
    // setInputValue("");
  };

  const handleApiCall = useCallback(
    async (serial: string) => {
      if (!serial) return;
      setLoading(true);
      setError(null);
      setScanResult(null);
      try {
        await fetchScanData(serial);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [fetchScanData]
  );

  const handleReset = useCallback(() => {
    setProductId(null);
    setInputValue("");
    setScanResult(null);
    setCountdown(null);
  }, []);

  useEffect(() => {
    if (inputValue.trim() === "") {
      setScanResult(null);
    }
  }, [inputValue]);

  useEffect(() => {
    if (!autoClear || !scanResult) {
      setCountdown(null);
      return;
    }

    setCountdown(autoClearDuration);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(countdownInterval);
          setInputValue("");
          setScanResult(null);
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
      setCountdown(null);
    };
  }, [scanResult, autoClear, autoClearDuration]);

  useEffect(() => {
    fetchProductOptions();
  }, [selectedCustomer]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value.trim().toUpperCase());
  };
  const handleSearch = (value: string) => {
    handleApiCall(value);
  };

  const fetchProductOptions = async () => {
    if (selectedCustomer) {
      try {
        setIsLoading(true);
        const requestPayload = {
          data: {
            currentPage: 1,
            pageSize: -1,
            customerId: selectedCustomer.value,
          },
        };

        const response = await post(GetProductList, requestPayload);
        const { dataResponse, data } = response;

        if (dataResponse.returnCode === eResultCode.SUCCESS) {
          const formattedOptions = data?.map((product: TProduct) => ({
            label: `${product.airtechModel} - ${product.bePartNo}`,
            value: product.id,
          }));

          setProductOptions(formattedOptions);
          setIsLoading(false);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: dataResponse.description,
          });
          setIsLoading(false);
        }
      } catch (error) {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: "Failed to fetch product data",
        });
        setIsLoading(false);
      }
    }
  };

  const containerClassName = useMemo(() => {
    const baseClass = styles.scannerContainer;
    let modeClass = "";
    switch (displayMode) {
      case "large":
        modeClass = styles.scannerModeLarge;
        break;
      case "xl":
        modeClass = styles.scannerModeXl;
        break;
      default:
        modeClass = styles.scannerModeDefault;
        break;
    }
    const fullscreenClass = isFullscreen ? styles.fullscreen : "";
    return `${baseClass} ${modeClass} ${fullscreenClass}`;
  }, [displayMode, isFullscreen]);

  const outputStyle = useMemo((): React.CSSProperties => {
    if (!scanResult) return {};
    return {
      backgroundColor:
        scanResult.outputStatus === eOUTPUT_STATUS.GREEN
          ? "var(--bs-success)"
          : "var(--bs-danger)",
      color: "white",
      textAlign: "center",
      padding: "0.5rem",
      borderRadius: "8px",
      transition: "background-color 0.3s ease",
      marginBottom: "1rem",
    };
  }, [scanResult]);

  const isDefaultMode = displayMode === "default";

  const assignCrate = async () => {
    try {
      const payload = {
        data: {
          id: selectedCrateId,
          scannedProductList: [{ id: scanResult?.id }],
        },
      };
      setIsLoading(true);

      const response = await post(UpdateCrateMapping, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        setSelectedCrateId(null);
        setScanResult(null);
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        padding: "1rem",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "scroll",
        gap: "1rem",
        transition: "flex-direction 0.5s ease",
      }}
    >
      <CustomModal
        title="Confirm"
        isOpen={showErrorModal}
        onOk={async () =>
          await fetchForcedScanData(
            productId as number,
            inputValue,
            selectedCustomer?.value as number
          )
        }
        onCancel={() => handleDeleteCancel()}
        okText="Proceed"
        cancelText="Close"
        cusHeader={true}
      >
        <p>{errorMessage}</p>
      </CustomModal>
      <CustomModal
        title="Confirm"
        isOpen={showOldSerialModal}
        onOk={async () =>
          await fetchForcedScanData(
            productId as number,
            inputValue,
            selectedCustomer?.value as number,
            true,
            isBothErrors ? true : false
          )
        }
        onCancel={() => handleDeleteCancel()}
        okText="Proceed"
        cancelText="Close"
        cusHeader={true}
      >
        <p>{errorMessage}</p>
      </CustomModal>
      <div
        style={{
          width: "100%",
          transition: "width 0.5s ease",
        }}
      >
        <div className={containerClassName}>
          <Card>
            {/* Settings panel remains the same */}
            <Row
              justify="start"
              align="middle"
              style={{ marginBottom: "1.5rem", gap: "1rem" }}
            >
              <Col>
                <Typography.Text
                  style={{ marginRight: "5px", fontSize: "12px" }}
                >
                  Customer Name
                </Typography.Text>
                <Select
                  size="small"
                  disabled={isLoading || customerOptions.length === 1}
                  showSearch
                  value={selectedCustomer}
                  placeholder="Select a customer"
                  options={customerOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value, option) => {
                    const selectedOption = option as {
                      label: string;
                      value: string;
                    };
                    setSelectedCustomer(selectedOption);
                    setScanResult(null);
                    setProductId(null);
                    setSelectedCrateId(null);
                    setInputValue("");
                  }}
                ></Select>
              </Col>
              <Col>
                <Space>
                  <Switch
                    checkedChildren="Blower Scan Activated"
                    unCheckedChildren="Crate Scan Activated"
                    checked={scanType === "blower"}
                    onChange={(checked) => {
                      setScanType(checked ? "blower" : "crate");
                      handleReset();
                    }}
                  />
                </Space>
              </Col>

              {Utils.isUserHasAccess(
                context.user?.privilegeList as TPrivilege[],
                ePrivileges.SCANSERIALNO
              ) ? (
                <CustomDrawer
                  trigger={
                    <Button type="link" disabled={isLoading}>
                      Bulk Unit Assign
                      <HistoryOutlined />
                    </Button>
                  }
                  component={ScannedBlowerList}
                  // componentProps={{
                  //   id: record.id,
                  // }}
                  placement="bottom"
                  title="Customer Form"
                  // width={1000}
                />
              ) : null}

              <Col>
                {Utils.isUserHasAccess(
                  context.user?.privilegeList as TPrivilege[],
                  ePrivileges.VIEWSCANHISTORY
                ) ? (
                  <CustomDrawer
                    trigger={
                      <Button type="link" disabled={isLoading}>
                        {scanType === "blower" ? "Unit" : "Crate"} Scanned
                        History
                        <HistoryOutlined />
                      </Button>
                    }
                    component={
                      scanType === "blower"
                        ? ScannedHistoryForm
                        : CrateScannedHistory
                    }
                    title="Scanned History Form"
                    size="default"
                  />
                ) : null}
              </Col>
            </Row>

            {/* Input Form remains the same */}
            <Form form={form} layout="vertical">
              <Row gutter={[16, 16]}>
                {scanType === "blower" && (
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label={<Title level={5}>Product</Title>}
                      rules={[
                        {
                          required: true,
                          message: "Please select a product!",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Select a product"
                        value={productId}
                        onChange={(value) => setProductId(value)}
                        options={productOptions}
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        loading={isLoading}
                        style={{ width: "100%" }}
                        size="large"
                      />
                    </Form.Item>

                    <Row
                      gutter={16}
                      style={{
                        position: "absolute",
                        top: 65,
                        left: 10,
                      }}
                    >
                      <Col>
                        {Utils.isUserHasAccess(
                          context.user?.privilegeList as TPrivilege[],
                          ePrivileges.VIEWSCANHISTORY
                        ) ? (
                          <Button
                            type="link"
                            disabled={isLoading}
                            onClick={handleReset}
                          >
                            Reset <GrPowerReset />
                          </Button>
                        ) : null}
                      </Col>
                    </Row>
                  </Col>
                )}

                <Col
                  xs={24}
                  sm={24}
                  md={scanType === "blower" ? 12 : 24} // 12 if blower, else full width
                >
                  <Form.Item
                    label={
                      scanType === "blower" ? (
                        <Title level={5}>Unit Serial Number</Title>
                      ) : (
                        <Title level={5}>Crate Serial Number</Title>
                      )
                    }
                  >
                    <Search
                      placeholder={`Scan or type ${
                        scanType === "blower" ? "unit" : "crate"
                      } serial number...`}
                      enterButton="Submit"
                      value={inputValue}
                      onChange={handleInputChange}
                      onSearch={handleSearch}
                      loading={loading}
                      autoFocus
                      size="large"
                      disabled={!productId && scanType === "blower"}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* --- MODIFIED: Results Display Area --- */}
            <div style={{ marginTop: "1rem" }}>
              {loading && (
                <Spin tip="Validating..." size="large">
                  <div style={{ height: "200px" }} />
                </Spin>
              )}
              {error && (
                <Alert
                  message="Error"
                  description={error}
                  type="error"
                  showIcon
                />
              )}
              {scanResult && (
                <div>
                  {(scanResult?.responseCode === eResultCode.R_DUPLICATE ||
                    (autoClear && countdown !== null)) && (
                    <Row
                      justify="space-between"
                      align="middle"
                      style={{ marginBottom: "1rem" }}
                    >
                      <Col>
                        {scanResult?.responseCode ===
                          eResultCode.R_DUPLICATE && (
                          <Space
                            direction="horizontal"
                            align="center"
                            size="middle"
                            style={{ padding: "10px" }}
                          >
                            <WarningOutlined
                              style={{
                                fontSize: "16px",
                                color: "var(--bs-danger)",
                              }}
                            />
                            <Title
                              level={5}
                              style={{
                                color: "var(--bs-danger)",
                                margin: 0,
                                textAlign: "center",
                              }}
                            >
                              {scanResult?.responseMsg}
                            </Title>
                          </Space>
                        )}
                      </Col>
                      <Col>
                        {autoClear && countdown !== null && (
                          <Title
                            level={5}
                            style={{
                              color: "#333",
                              margin: 0,
                              textAlign: "center",
                            }}
                          >
                            {`Auto clearing in ${countdown}s...`}
                          </Title>
                        )}
                      </Col>
                    </Row>
                  )}
                  <div style={outputStyle}>
                    <Title level={2} style={{ color: "white", margin: 0 }}>
                      {scanType === "blower"
                        ? scanResult.warrantyMessage
                        : "Crate Scan Successful"}
                    </Title>
                  </div>
                  {scanType === "blower" && (
                    <>
                      <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="Unit Serial Number">
                          {scanResult.serialNumber}
                        </Descriptions.Item>
                        <Descriptions.Item label="Present in Customer Excel">
                          {scanResult.excelMessage}
                        </Descriptions.Item>
                        <Descriptions.Item label="Debrief Date">
                          {scanResult?.debriefDateString
                            ? scanResult?.debriefDateString
                            : "N/A"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Manufacturing Date">
                          {scanResult?.manufacturingDateString
                            ? scanResult?.manufacturingDateString
                            : "N/A"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Warranty Date">
                          {scanResult?.warrantyDateString
                            ? scanResult?.warrantyDateString
                            : "N/A"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Scanned/Received Date">
                          {scanResult?.scannedDate
                            ? DateUtils.format(
                                scanResult?.scannedDate,
                                context?.user?.dateFormat,
                                true
                              )
                            : "N/A"}
                        </Descriptions.Item>
                      </Descriptions>
                      <Form
                        form={form}
                        name="register"
                        layout="vertical"
                        autoComplete="off"
                      >
                        <Row
                          gutter={16}
                          className="formRowHeight"
                          style={{ marginTop: "20px" }}
                        >
                          <Col span={12}>
                            <Form.Item name="crateId" label={`Assign Crate`}>
                              <Select
                                value={selectedCrateId}
                                size="large"
                                disabled={isLoading}
                                placeholder="Select crate"
                                options={crateOptions}
                                onChange={(value) => setSelectedCrateId(value)}
                                showSearch
                                filterOption={(input, option) =>
                                  (option?.label ?? "")
                                    ?.toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                optionRender={(option) => (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div>{option.data.label}</div>
                                      <Progress
                                        percent={
                                          (option.data.data.blowerCount /
                                            option.data.data.crateCapacity) *
                                          100
                                        }
                                        showInfo={false}
                                      />
                                    </div>
                                    <div style={{ marginLeft: "10px" }}>
                                      {`${option.data.data.blowerCount}/${option.data.data.crateCapacity}`}
                                    </div>
                                  </div>
                                )}
                              />
                            </Form.Item>
                            {/* </Form> */}
                          </Col>
                          {selectedCrateId &&
                          Utils.isUserHasAccess(
                            context.user?.privilegeList as TPrivilege[],
                            ePrivileges.ASSIGNCRATE
                          ) ? (
                            <Col span={6} style={{ marginTop: "26px" }}>
                              <Button
                                size="large"
                                type="primary"
                                onClick={async () => await assignCrate()}
                                disabled={isLoading}
                                loading={isLoading}
                                // className="btnDimensions"
                              >
                                Assign
                              </Button>
                            </Col>
                          ) : null}
                        </Row>
                      </Form>
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ScannerForm;
